import { useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { SplashScreenType } from '@/app/models/SplashScreenType';
import { type SplashScreenAppearance } from '@/app/models/SplashScreenAppearance';
import { InterfaceAppearanceStyles } from './InterfaceAppearanceStyles';
import { useInterfaceAppearance } from './useInterfaceAppearance';
import { SplashScreenMedia } from './SplashScreenMedia';
import { clearSplashAppearance, readSplashAppearance, saveSplashAppearance } from './core/_splashAppearance';
import { getSidebarColorTokens } from './core/_utils';
import { useSplashStartup } from './useSplashStartup';
import { DEFAULT_SPLASH_ANIMATION } from '@/app/consts/interfaceAppearance';
import clsx from 'clsx';

type InterfaceAppearanceProps = {
  children?: (loading: boolean) => ReactNode;
};

export const InterfaceAppearance = ({ children }: InterfaceAppearanceProps) => {
  const { appearance, hasSavedAppearance, loadingAppearance } = useInterfaceAppearance();
  const { auth, currentUser } = useAuth();
  const sessionToken = auth?.token;
  const systemName = appearance.system_name;
  const splashType =
    appearance.splash_type?.value === SplashScreenType.LOTTIE ? SplashScreenType.LOTTIE : SplashScreenType.IMAGE;
  const mediaUrl = appearance.splash_media?.public_url;
  const backgroundColor = appearance.splash_background_color || '#ffffff';
  const darkBackgroundColor = appearance.splash_dark_background_color || '#000000';
  const savedSplash = useMemo<SplashScreenAppearance>(
    () => ({
      type: splashType,
      mediaUrl,
      backgroundColor,
      darkBackgroundColor,
    }),
    [splashType, mediaUrl, backgroundColor, darkBackgroundColor],
  );
  const cachedSplash = readSplashAppearance(sessionToken);
  const hasResolvedAppearance = hasSavedAppearance || !sessionToken;
  const splash = hasResolvedAppearance ? savedSplash : cachedSplash;
  const effectiveSource = splash?.mediaUrl || DEFAULT_SPLASH_ANIMATION;
  const sourceKey = `${splash?.type || SplashScreenType.LOTTIE}:${effectiveSource}`;
  const authenticated = Boolean(sessionToken);
  const { visible, onMediaReady } = useSplashStartup(authenticated, currentUser?.id, loadingAppearance, sourceKey);
  const splashClasses = clsx('splash-screen', { 'splash-screen-initializing': visible });
  const lightTokens = getSidebarColorTokens(splash?.backgroundColor || '#ffffff');
  const darkTokens = getSidebarColorTokens(splash?.darkBackgroundColor || '#000000');
  const splashStyle = {
    '--splash-background': lightTokens['--app-sidebar-background'],
    '--splash-background-dark': darkTokens['--app-sidebar-background'],
    '--splash-foreground': lightTokens['--app-sidebar-foreground'],
    '--splash-foreground-dark': darkTokens['--app-sidebar-foreground'],
  } as CSSProperties;

  useEffect(() => {
    if (!sessionToken) {
      clearSplashAppearance();
      return;
    }
    if (hasSavedAppearance) {
      saveSplashAppearance(sessionToken, savedSplash);
    }
  }, [sessionToken, hasSavedAppearance, savedSplash]);

  useEffect(() => {
    globalThis.dispatchEvent(new Event('interface-splash-mounted'));
  }, []);

  useEffect(() => {
    const hasDefaultTitle = document.title.endsWith(' — Backoffice');
    if (hasDefaultTitle) document.title = `${systemName} — Backoffice`;
  }, [systemName]);

  return (
    <>
      <InterfaceAppearanceStyles />
      <section aria-label="Carregando sistema" className={splashClasses} id="splash-screen-react" style={splashStyle}>
        {splash ? (
          <SplashScreenMedia
            source={splash.mediaUrl}
            systemName={systemName}
            type={splash.type}
            onReady={onMediaReady}
          />
        ) : null}
        <div className="loader-wrapper">
          <span className="loader" />
          <span className="loading-text">Carregando...</span>
        </div>
      </section>
      {children?.(visible)}
    </>
  );
};
